import { IAP_CATALOG } from "./iap-rewards";
/**
 * In-game news: schema + validator for the news model stored in D1's
 * `news_post` table (migrations/003_news.sql — see this plan item's
 * README.md "The data model"). Pure — no React, no fetch — so it can run
 * client-side for live feedback in the `/news` editor AND server-side as the
 * write-time gate (`server/index.ts`'s news routes import this module
 * directly), matching how `trial-schema.ts` is used for `EC2_TRIAL_STAGE_3.0`.
 *
 * `NewsIndexEntry` is the listing shape (titles only, never bodies) that
 * every post's row is reduced to for `validateNewsIndex`; `NewsPost` is one
 * post's bodies, validated separately by `validateNewsPost`. The server
 * builds both from the same D1 row on every write — see server/index.ts's
 * `candidateIndexWith`. `id` is the per-player news-state join key
 * (`player_news_state.news_id`) — it is author-assigned and permanent, which
 * is why format is validated but the value itself is never auto-generated here.
 *
 * `en` and `id` are the only supported languages — the same two the game's
 * localization tables actually carry copy for (see README.md). A missing
 * `id` string is a warning, never an error: the client always falls back to
 * `en`, never to blank.
 *
 * Warnings never block save. Errors do.
 */

export const NEWS_LANGUAGES = ["en", "id"] as const;
export type NewsLanguage = (typeof NEWS_LANGUAGES)[number];

/** Also the id charset enforced on create — see server/index.ts. */
export const NEWS_ID_PATTERN = /^[a-z0-9-]+$/;

/** Strict UTC ISO-8601 — must carry an explicit `Z`. No bare local-time strings. */
const UTC_ISO_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?Z$/;

// ─── Shape ───────────────────────────────────────────────────────────────

export interface NewsIndexEntry {
  id: string;
  titles: Partial<Record<NewsLanguage, string>>;
  publishAt: string | null;
  expireAt: string | null;
  pinned: boolean;
  draft: boolean;
  order: number;
}

export type NewsIndex = NewsIndexEntry[];

export interface NewsPost {
  id: string;
  bodies: Partial<Record<NewsLanguage, string>>;
}

/** Same numeric Reward shape the game deserializes as List<Reward>. */
export interface NewsReward {
  iapProductId?: string;
  rewardType: number;
  amount: number;
  itemID?: string;
  hero?: number;
  developerBuffID?: string;
  recipe?: number;
}

export interface NewsIssue {
  severity: "error" | "warning";
  path: string;
  message: string;
}

const INDEX_ENTRY_KEYS = new Set(["id", "titles", "publishAt", "expireAt", "pinned", "draft", "order"]);
const POST_KEYS = new Set(["id", "bodies"]);

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

function isUtcIsoOrNull(v: unknown): v is string | null {
  if (v === null) return true;
  return typeof v === "string" && UTC_ISO_PATTERN.test(v) && !Number.isNaN(Date.parse(v));
}

// ─── Index validator ───────────────────────────────────────────────────────

export function validateNewsIndex(parsed: unknown): NewsIssue[] {
  const issues: NewsIssue[] = [];

  if (!Array.isArray(parsed)) {
    issues.push({ severity: "error", path: "", message: "News index must be a JSON array." });
    return issues;
  }

  const seenIds = new Set<string>();

  parsed.forEach((raw, i) => {
    const p = `[${i}]`;

    if (!isPlainObject(raw)) {
      issues.push({ severity: "error", path: p, message: "Entry is not an object." });
      return;
    }

    // Rule 7 — unknown top-level keys rejected.
    for (const key of Object.keys(raw)) {
      if (!INDEX_ENTRY_KEYS.has(key)) {
        issues.push({ severity: "error", path: `${p}.${key}`, message: `Unknown key "${key}".` });
      }
    }

    // Rule 1 — id format + uniqueness.
    const id = raw.id;
    if (!isNonEmptyString(id) || !NEWS_ID_PATTERN.test(id)) {
      issues.push({
        severity: "error",
        path: `${p}.id`,
        message: "id must be a non-empty string matching ^[a-z0-9-]+$.",
      });
    } else if (seenIds.has(id)) {
      issues.push({ severity: "error", path: `${p}.id`, message: `Duplicate id "${id}" — ids must be unique.` });
    } else {
      seenIds.add(id);
    }

    // Rule 2 — titles.en required.
    const titles = raw.titles;
    if (!isPlainObject(titles) || !isNonEmptyString(titles.en)) {
      issues.push({ severity: "error", path: `${p}.titles.en`, message: "titles.en must be a non-empty string." });
    }
    // Warning rule 4 — missing id title falls back to en.
    if (isPlainObject(titles) && !isNonEmptyString(titles.id)) {
      issues.push({
        severity: "warning",
        path: `${p}.titles.id`,
        message: "No Indonesian title — players will see the English title instead.",
      });
    }

    // Rule 4 — publishAt / expireAt shape.
    const publishAt = raw.publishAt;
    const expireAt = raw.expireAt;
    if (!isUtcIsoOrNull(publishAt)) {
      issues.push({
        severity: "error",
        path: `${p}.publishAt`,
        message: "publishAt must be null or a UTC ISO-8601 string (e.g. 2026-08-26T09:00:00Z).",
      });
    }
    if (!isUtcIsoOrNull(expireAt)) {
      issues.push({
        severity: "error",
        path: `${p}.expireAt`,
        message: "expireAt must be null or a UTC ISO-8601 string (e.g. 2026-08-26T09:00:00Z).",
      });
    }

    // Rule 5 — ordering, only when both parsed cleanly.
    if (isUtcIsoOrNull(publishAt) && publishAt && isUtcIsoOrNull(expireAt) && expireAt) {
      if (!(Date.parse(expireAt) > Date.parse(publishAt))) {
        issues.push({ severity: "error", path: `${p}.expireAt`, message: "expireAt must be after publishAt." });
      }
    }

    // Warning rule 1 — already-expired post is invisible in game.
    if (isUtcIsoOrNull(expireAt) && expireAt && Date.parse(expireAt) < Date.now()) {
      issues.push({
        severity: "warning",
        path: `${p}.expireAt`,
        message: "expireAt is already in the past — this post is invisible in game.",
      });
    }

    // Rule 6 — pinned / draft booleans, order integer.
    if (typeof raw.pinned !== "boolean") {
      issues.push({ severity: "error", path: `${p}.pinned`, message: "pinned must be a boolean." });
    }
    if (typeof raw.draft !== "boolean") {
      issues.push({ severity: "error", path: `${p}.draft`, message: "draft must be a boolean." });
    }
    if (typeof raw.order !== "number" || !Number.isInteger(raw.order)) {
      issues.push({ severity: "error", path: `${p}.order`, message: "order must be an integer." });
    }

    // Warning rule 2 — a scheduled (non-draft) post too far out is probably a mistake.
    if (
      raw.draft === false &&
      isUtcIsoOrNull(publishAt) &&
      publishAt &&
      Date.parse(publishAt) - Date.now() > ONE_YEAR_MS
    ) {
      issues.push({
        severity: "warning",
        path: `${p}.publishAt`,
        message: "publishAt is more than a year out for a non-draft post — check this is intentional.",
      });
    }

    // Warning rule 3 (size-near-cap) is intentionally skipped: the real
    // PlayFab per-value size cap could not be established without live
    // credentials — see this session's handoff. Do not invent a number.
  });

  return issues;
}

// ─── Post body validator ────────────────────────────────────────────────────

export function validateNewsPost(parsed: unknown, expectedId: string): NewsIssue[] {
  const issues: NewsIssue[] = [];

  if (!isPlainObject(parsed)) {
    issues.push({ severity: "error", path: "", message: "Post body is not an object." });
    return issues;
  }

  // Rule 7 — unknown top-level keys rejected.
  for (const key of Object.keys(parsed)) {
    if (!POST_KEYS.has(key)) {
      issues.push({ severity: "error", path: key, message: `Unknown key "${key}".` });
    }
  }

  // Rule 8 — the post body's own id matches the index row it is saved with.
  if (parsed.id !== expectedId) {
    issues.push({
      severity: "error",
      path: "id",
      message: `Post id "${String(parsed.id)}" does not match its index entry id "${expectedId}".`,
    });
  }

  // Rule 3 — bodies.en required.
  const bodies = parsed.bodies;
  if (!isPlainObject(bodies) || !isNonEmptyString(bodies.en)) {
    issues.push({ severity: "error", path: "bodies.en", message: "bodies.en must be a non-empty string." });
  }
  // Warning rule 4 — missing id body falls back to en.
  if (isPlainObject(bodies) && !isNonEmptyString(bodies.id)) {
    issues.push({
      severity: "warning",
      path: "bodies.id",
      message: "No Indonesian body — players will see the English body instead.",
    });
  }

  return issues;
}

/** Optional attachment list on a news post. Empty / missing is fine (announcement only). */
export function validateNewsRewards(parsed: unknown): NewsIssue[] {
  const issues: NewsIssue[] = [];
  if (parsed === undefined || parsed === null) return issues;

  if (!Array.isArray(parsed)) {
    issues.push({ severity: "error", path: "rewards", message: "rewards must be an array." });
    return issues;
  }

  parsed.forEach((raw, i) => {
    const p = `rewards[${i}]`;
    if (!isPlainObject(raw)) {
      issues.push({ severity: "error", path: p, message: "Reward entry is not an object." });
      return;
    }
    if (typeof raw.rewardType !== "number" || !Number.isInteger(raw.rewardType)) {
      issues.push({ severity: "error", path: `${p}.rewardType`, message: "rewardType must be an integer." });
    }
    if (raw.rewardType === 100) {
      const product = IAP_CATALOG.find((product) => product.id === raw.iapProductId);
      if (!product || product.unavailable) issues.push({ severity: "error", path: `${p}.iapProductId`, message: product?.unavailable ?? "Select a valid IAP product." });
      if (!Number.isSafeInteger(raw.amount) || (raw.amount as number) < 1 || (raw.amount as number) > 100) issues.push({ severity: "error", path: `${p}.amount`, message: "IAP quantity must be an integer from 1 to 100." });
    }
    if (typeof raw.amount !== "number" || !Number.isFinite(raw.amount)) {
      issues.push({ severity: "error", path: `${p}.amount`, message: "amount must be a number." });
    }
  });

  return issues;
}
